import type { Source } from '@/data/sources';

import styles from './SourceAttribution.module.css';

export type SourceAttributionDensity = 'default' | 'compact';

interface SourceAttributionProps {
  /**
   * Sources to credit. Single-entry today (TSG); the prop is a list so
   * multi-source / tabbed rendering can layer on without a component rewrite.
   */
  sources: Source[];
  /**
   * Optional eyebrow label rendered before the sources. Defaults to "Sourced from".
   * Pass an empty string to hide it.
   */
  label?: string;
  /**
   * `default` for inline use under content blocks; `compact` for tooltip
   * interiors where space is tight (hides the caption row, shrinks the logo).
   */
  density?: SourceAttributionDensity;
  className?: string;
}

const cn = (...parts: (string | undefined | false)[]) =>
  parts.filter(Boolean).join(' ');

/**
 * Credits the content authority behind a Why This Matters section, tooltip
 * body, or rationale display. Source-agnostic by design: the component never
 * references TSG (or any other partner) directly, so the same code path
 * handles future sources (Mariana Health, Open Evidence, customer content)
 * without changes.
 */
export function SourceAttribution({
  sources,
  label = 'Sourced from',
  density = 'default',
  className,
}: SourceAttributionProps) {
  if (sources.length === 0) return null;

  return (
    <div className={cn(styles.root, density === 'compact' && styles.compact, className)}>
      {label && <span className={styles.label}>{label}</span>}
      <span className={styles.sources}>
        {sources.map((source) => {
          const inner = (
            <>
              <img
                className={styles.logo}
                src={source.logoSrc}
                alt={source.logoAlt}
                loading="lazy"
              />
              <span className={styles.text}>
                <span className={styles.name}>{source.name}</span>
                {source.caption && (
                  <span className={styles.caption}>{source.caption}</span>
                )}
              </span>
            </>
          );

          if (source.href) {
            return (
              <a
                key={source.id}
                className={styles.source}
                href={source.href}
                target="_blank"
                rel="noopener noreferrer"
              >
                {inner}
              </a>
            );
          }

          return (
            <span key={source.id} className={styles.source}>
              {inner}
            </span>
          );
        })}
      </span>
    </div>
  );
}
